/* eslint-disable no-redeclare */

// Migrated to ES6 class + ol@10 compat:
// - getUid: ol/index.js → ol/util.js
// - SourceState enum → string literal
// - TileEventType enum → string literal
// - listen/unlisten: unlisten foi removido em ol@7; usar unlistenByKey(key)
import { getUid } from 'ol/util.js';
import { extentFromProjection, createXYZ } from 'ol/tilegrid.js';
import { getKeyZXY } from 'ol/tilecoord.js';
import { listen, unlistenByKey } from 'ol/events.js';

import * as proj from 'ol/proj.js';
import TileSource, { TileSourceEvent } from 'ol/source/Tile.js';
import TileState from 'ol/TileState.js';
import EventType from '../events/EventType.js';

import CanvasTile from '../CanvasTile.js';
import FeatureGeomType from '../lov/FeatureGeomType.js';

const TILE_SIZE = 256; // tile image size
const CHROME_BATCH_SIZE = 200; // batch size for chrome rendering

const TRANSPARENT_1X1_PNG_IMAGE = new Image();
TRANSPARENT_1X1_PNG_IMAGE.src = require('../transparent_1px.png');

function isChrome() {
  var isChromium = window.chrome,
    winNav = window.navigator,
    vendorName = winNav.vendor,
    isOpera = winNav.userAgent.indexOf('OPR') > -1,
    isIEedge = winNav.userAgent.indexOf('Edge') > -1,
    isIOSChrome = winNav.userAgent.match('CriOS');

  if (isIOSChrome) {
    return true;
  } else if (
    isChromium !== null &&
    typeof isChromium !== 'undefined' &&
    vendorName === 'Google Inc.' &&
    isOpera === false &&
    isIEedge === false
  ) {
    return true;
  } else {
    return false;
  }
}

function createRoundSymbol(radius, lineWidth, strokeStyle, fillStyle) {
  var offset = radius + lineWidth + 2;

  var canvasSymbol = document.createElement('canvas');
  canvasSymbol.width = offset * 2;
  canvasSymbol.height = offset * 2;

  var context = canvasSymbol.getContext('2d');

  context.lineWidth = lineWidth;
  context.strokeStyle = strokeStyle;
  context.fillStyle = fillStyle;

  context.imageSmoothingEnabled = true;
  context.mozImageSmoothingEnabled = true;
  context.webkitImageSmoothingEnabled = true;
  context.msImageSmoothingEnabled = true;

  context.beginPath();
  context.arc(offset, offset, radius, 0, 2 * Math.PI, false);
  context.fill();
  context.stroke();

  return {
    image: context.canvas,
    offsetX: -offset,
    offsetY: -offset,
  };
}

class SelectionLayerTileSource extends TileSource {
  constructor(options) {
    options = options || {};
    options.transition = 0;

    super({
      attributions: options.attributions,
      cacheSize: options.cacheSize,
      extent: options.extent,
      opaque: options.opaque,
      projection: proj.get('EPSG:3857'),
      state: options.state,
      tileGrid: options.tileGrid,
      tilePixelRatio: options.tilePixelRatio,
      wrapX: options.wrapX === undefined ? true : options.wrapX,
      transition: options.transition,
    });

    this.tileLoadFunction = options.tileLoadFunction
      ? options.tileLoadFunction
      : (tile) => this.defaultTileLoadFunction(tile);

    this._visible = true;
    this._tileLoadingKeys = {};
    this._styleList = [null, null, null];
    this._featureSources = [];
    this._refreshToken = 0;

    // Listener keys agrupados por source — pra cleanup com unlistenByKey
    this._sourceListenerKeys = new Map();
    // Listener keys de tiles individuais — pra cleanup
    this._tileListenerKeys = new Map();

    var sourceProjection = this.getProjection();
    var extent = extentFromProjection(sourceProjection);

    var tileGrid = createXYZ({
      extent: extent,
      minZoom: 0,
      maxZoom: 23,
      tileSize: TILE_SIZE / (this._hidpi ? 2 : 1),
    });

    this.tileGrid = tileGrid;

    this.setState('ready'); // SourceState.READY
  }

  // ============== Properties ==============

  getTileLoadFunction() {
    return this.tileLoadFunction;
  }

  setTileLoadFunction(tileLoadFunction) {
    this.tileLoadFunction = tileLoadFunction;
    this.refresh(); // ol@10: clear + changed
  }

  getTile(z, x, y, pixelRatio, projection, tileCache) {
    var sourceProjection = this.getProjection();
    if (tileCache) this._lastTileCache = tileCache;
    return this.getTileInternal(z, x, y, pixelRatio, sourceProjection || projection, tileCache);
  }

  /**
   * ol@10: ainda chamado por alguns paths; opera sobre tileCache passado externamente.
   */
  useTile(z, x, y, tileCache) {
    if (!tileCache) return;
    var tileCoordKey = getKeyZXY(z, x, y);
    if (tileCache.containsKey(tileCoordKey)) {
      tileCache.get(tileCoordKey);
    }
  }

  setVisibility(visible) {
    this._visible = visible;

    if (!visible)
      this.clearSelection();
  }

  visible() {
    return !!this._visible;
  }

  setStyle(pointStyle, lineStyle, polygonStyle) {
    this._styleList = [pointStyle, lineStyle, polygonStyle];
    this._defaultSymbol = null;
    this.refresh();
  }

  /**
   * Adiciona um FeatureLayerWorkerTileSource pra ser observado.
   * @param {FeatureLayerWorkerTileSource} source
   */
  addSource(source) {
    var idx = this._featureSources.indexOf(source);
    if (idx >= 0)
      return;

    var keys = [
      listen(source, EventType.CLEAR, this.handleSourceEvents, this),
      listen(source, EventType.SELECTION_CHANGE, this.handleSourceEvents, this),
      listen(source, EventType.VISIBILITY_CHANGE, this.handleSourceEvents, this),
    ];
    this._sourceListenerKeys.set(source, keys);

    this._featureSources.push(source);

    if (!source.visible())
      return;

    if (source.selectedCount() === 0)
      return;

    this.dispatchEvent(EventType.CLEAR);
    this.refresh();
  }

  removeSource(source) {
    var idx = this._featureSources.indexOf(source);
    if (idx < 0)
      return;

    var keys = this._sourceListenerKeys.get(source);
    if (keys) {
      keys.forEach((k) => unlistenByKey(k));
      this._sourceListenerKeys.delete(source);
    }

    this._featureSources.splice(idx, 1);

    if (!source.visible())
      return;

    if (source.selectedCount() === 0)
      return;

    this.dispatchEvent(EventType.CLEAR);
    this.refresh();
  }

  clearSources() {
    if (this._featureSources.length === 0)
      return;

    var redraw = false;

    for (var source of this._featureSources) {
      var keys = this._sourceListenerKeys.get(source);
      if (keys) {
        keys.forEach((k) => unlistenByKey(k));
      }

      if (redraw || !source.visible() || source.selectedCount() === 0)
        continue;

      redraw = true;
    }

    this._sourceListenerKeys.clear();
    this._featureSources = [];

    if (redraw) {
      this.dispatchEvent(EventType.CLEAR);
      this.refresh();
    }
  }

  // ============== Event Handlers ==============

  handleTileChange(event) {
    var tile = event.target;
    var uid = getUid(tile);
    var tileState = tile.getState();
    var type;
    if (tileState === TileState.LOADING) {
      this._tileLoadingKeys[uid] = true;
      type = 'tileloadstart';
    } else if (uid in this._tileLoadingKeys) {
      delete this._tileLoadingKeys[uid];
      type =
        tileState === TileState.ERROR
          ? 'tileloaderror'
          : tileState === TileState.LOADED || tileState === TileState.ABORT
          ? 'tileloadend'
          : undefined;
    }
    if (type !== undefined) {
      this.dispatchEvent(new TileSourceEvent(type, tile));
    }
  }

  handleSourceEvents(event) {
    if (event.type === EventType.VISIBILITY_CHANGE && event.target.selectedCount() === 0) {
      return;
    }

    var currentToken = ++this._refreshToken;

    setTimeout(() => {
      if (this._refreshToken !== currentToken)
        return;

      this.dispatchEvent(EventType.CLEAR);
      this.refresh();
    }, 100);
  }

  // ============== Methods ==============

  defaultTileLoadFunction(canvasTile) {
    return this._renderTileIndexContent(canvasTile);
  }

  /**
   * ol@10: tileCache passa como parâmetro (não é mais property do source).
   */
  getTileInternal(z, x, y, pixelRatio, projection, tileCache) {
    var tile = null;
    var tileCoordKey = getKeyZXY(z, x, y);
    var sourceKey = 'CHAVE_DE_VERSAO';

    if (!tileCache || !tileCache.containsKey(tileCoordKey)) {
      tile = this._createTile(z, x, y, pixelRatio, projection, sourceKey);
      if (tileCache) tileCache.set(tileCoordKey, tile);
    } else {
      tile = tileCache.get(tileCoordKey);

      if (tile.key !== sourceKey) {
        var interimTile = tile;
        tile = this._createTile(z, x, y, pixelRatio, projection, sourceKey);

        if (interimTile.getState() === TileState.IDLE) {
          tile.interimTile = interimTile.interimTile;
        } else {
          tile.interimTile = interimTile;
        }
        tile.refreshInterimChain();
        tileCache.replace(tileCoordKey, tile);
      }
    }

    return tile;
  }

  _createTile(z, x, y, pixelRatio, projection, sourceKey) {
    var tileCoord = [z, x, y];
    var withinExtent = this.getTileCoordForTileUrlFunction(tileCoord, projection);

    var tileState = withinExtent ? TileState.IDLE : TileState.EMPTY;

    var tile = new CanvasTile(
      tileCoord,
      tileState,
      this.tileLoadFunction,
      true, // wait load
      this.tileOptions,
    );

    tile.key = sourceKey;

    var key = listen(tile, EventType.CHANGE, this.handleTileChange, this);
    this._tileListenerKeys.set(tile, key);

    return tile;
  }

  _renderTileIndexContent(canvasTile) {
    let tileCoord = canvasTile.getTileCoord();
    let urlTileCoord = this._generateUrlTileCoord(tileCoord);

    var sourcesByGeomType = {
      [FeatureGeomType.POLYGON]: [],
      [FeatureGeomType.POLYLINE]: [],
      [FeatureGeomType.POINT]: [],
    };

    for (var source of this._featureSources) {
      if (!source.initialized()) continue;
      if (!source.visible()) continue;
      if (!source.selectedCount()) continue;

      sourcesByGeomType[source.getGeomType()].push(source);
    }

    var asyncData = {
      dataList: [],
      queueSize: 0,
    };

    for (var source of sourcesByGeomType[FeatureGeomType.POLYGON]) {
      this._asyncGetTileData(source, asyncData, canvasTile, urlTileCoord);
    }

    for (var source of sourcesByGeomType[FeatureGeomType.POLYLINE]) {
      this._asyncGetTileData(source, asyncData, canvasTile, urlTileCoord);
    }

    for (var source of sourcesByGeomType[FeatureGeomType.POINT]) {
      this._asyncGetTileData(source, asyncData, canvasTile, urlTileCoord);
    }

    if (asyncData.dataList.length === 0) {
      canvasTile.setLoadError();
      return true;
    }

    return false;
  }

  _asyncGetTileData(source, asyncData, canvasTile, urlTileCoord) {
    var dataIdx = asyncData.dataList.length;

    asyncData.dataList.push(null);
    asyncData.queueSize++;

    source
      .postWithPromise('get_tile', {
        Z: urlTileCoord[0],
        X: urlTileCoord[1],
        Y: urlTileCoord[2],
        filter: 'selected',
      })
      .then(
        (data) => {
          asyncData.dataList[dataIdx] = data;
          asyncData.queueSize--;

          if (asyncData.queueSize > 0) return;

          if (canvasTile.state === TileState.ERROR) {
            canvasTile.reset();
          }

          if (canvasTile.state !== TileState.LOADING && canvasTile.state !== TileState.IDLE) {
            return;
          }

          if (this._renderTileContent(canvasTile, asyncData.dataList)) {
            canvasTile.setLoadSuccess();
          } else {
            canvasTile.setLoadError();
          }
        },
        (data) => {
          console.log('get_tile Falhou (' + data + ')');

          if (canvasTile.state === TileState.ERROR) return;
          if (canvasTile.state !== TileState.LOADING) return;

          canvasTile.setLoadError();
        },
      );
  }

  _renderTileContent(canvasTile, dataList) {
    var drawn = false;
    var context;

    for (var data of dataList) {
      var linearData =
        data.type === 'int16'
          ? new Int16Array(data.geometryBuffer)
          : new Float32Array(data.geometryBuffer);

      if (!linearData.length) continue;

      if (!drawn) {
        drawn = true;

        context = canvasTile.getImage().getContext('2d');

        context.imageSmoothingEnabled = true;
        context.mozImageSmoothingEnabled = true;
        context.webkitImageSmoothingEnabled = true;
        context.msImageSmoothingEnabled = true;

        context.miterLimit = 2;

        context.save();

        context.clearRect(0, 0, TILE_SIZE, TILE_SIZE);
      }

      // Feature type: 1=point, 2=line, 3=polygon
      switch (linearData[0]) {
        case 1:
          this._renderPoints(context, linearData);
          break;
        case 2:
          if (isChrome())
            this._renderLinesForChrome(context, linearData);
          else
            this._renderLines(context, linearData);
          break;
        case 3:
          this._renderPolygons(context, linearData);
          break;
        default:
          break;
      }
    }

    if (!drawn) return false;

    context.restore();

    return true;
  }

  _renderPoints(context, linearData) {
    const pointStyleIdx = 0;

    if (!this._defaultSymbol) {
      this._symbolList = this._styleList[0] ? [this._styleList[0].symbol] : [];
      this._defaultSymbol = createRoundSymbol(5, 1, 'rgba(66, 66, 200, 0.8)', '#00FFFF');
    }

    context.drawImage(TRANSPARENT_1X1_PNG_IMAGE, 0, 0);

    var currSymbol = this._symbolList[pointStyleIdx] || this._defaultSymbol;

    var idx = 1;
    var qtd = linearData.length;

    while (idx < qtd) {
      var px = linearData[idx++] + currSymbol.offsetX;
      var py = linearData[idx++] + currSymbol.offsetY;
      context.drawImage(currSymbol.image, px, py);
    }
  }

  _renderLinesForChrome(context, linearData) {
    const lineStyleIdx = 1;

    var defaultStyle = {
      lineWidth: 3,
      strokeStyle: '#00FFFF',
    };

    var currStyle = this._styleList[lineStyleIdx] || defaultStyle;

    context.lineWidth = currStyle.lineWidth;
    context.strokeStyle = currStyle.strokeStyle;

    var path2d = new Path2D();
    var renderCheck = CHROME_BATCH_SIZE;

    var idx = 1;
    var qtd = linearData.length;

    while (idx < qtd) {
      var coordCount = linearData[idx++];

      var px = linearData[idx++];
      var py = linearData[idx++];

      path2d.moveTo(px, py);

      for (var k = 1; k < coordCount; k++) {
        px = linearData[idx++];
        py = linearData[idx++];

        path2d.lineTo(px, py);
      }

      if (--renderCheck <= 0) {
        context.stroke(path2d);
        path2d = new Path2D();
        renderCheck = CHROME_BATCH_SIZE;
      }
    }

    if (renderCheck < CHROME_BATCH_SIZE) {
      context.stroke(path2d);
    }
  }

  _renderLines(context, linearData) {
    const lineStyleIdx = 1;

    var defaultStyle = {
      lineWidth: 3.5,
      strokeStyle: '#00FFFF',
    };

    var currStyle = this._styleList[lineStyleIdx] || defaultStyle;

    context.lineWidth = currStyle.lineWidth;
    context.strokeStyle = currStyle.strokeStyle;

    var idx = 1;
    var qtd = linearData.length;

    while (idx < qtd) {
      context.beginPath();

      var coordCount = linearData[idx++];

      var px = linearData[idx++];
      var py = linearData[idx++];

      context.moveTo(px, py);

      for (var k = 1; k < coordCount; k++) {
        px = linearData[idx++];
        py = linearData[idx++];

        context.lineTo(px, py);
      }

      context.stroke();
    }
  }

  _renderPolygons(context, linearData) {
    const polygonStyleIdx = 2;

    var defaultStyle = {
      lineWidth: 2,
      strokeStyle: '#00FFFF',
      fillStyle: 'rgba(0,255,255,0.2)',
    };

    var currStyle = this._styleList[polygonStyleIdx] || defaultStyle;

    context.lineWidth = currStyle.lineWidth;
    context.strokeStyle = currStyle.strokeStyle;
    context.fillStyle = currStyle.fillStyle;

    var idx = 1;
    var qtd = linearData.length;

    while (idx < qtd) {
      context.beginPath();
      var ringCount = linearData[idx++];

      for (var j = 0; j < ringCount; j++) {
        var coordCount = linearData[idx++];

        var px = linearData[idx++];
        var py = linearData[idx++];

        context.moveTo(px, py);

        for (var k = 1; k < coordCount; k++) {
          px = linearData[idx++];
          py = linearData[idx++];

          context.lineTo(px, py);
        }
      }

      context.closePath();

      context.fill('evenodd');
      context.stroke();
    }
  }

  // ============== Utilitary ==============

  _generateUrlTileKey(sourceKey, z, x, y) {
    return sourceKey + '_' + z + '_' + x + '_' + y;
  }

  _generateUrlTileCoord(tileCoord) {
    var wrappedCoord = this.getTileCoordForTileUrlFunction(tileCoord);
    return [wrappedCoord[0], wrappedCoord[1], -wrappedCoord[2] - 1];
  }
}

export default SelectionLayerTileSource;
